import type {
  PaginationState,
  SortingState,
  Updater,
} from "@tanstack/react-table";

import { useMemo } from "react";

import type { ReturnedTranslatedContentClientsPageHook } from "../../types";

import { useIsMobile } from "../../../hooks";
import { TableDesktop, TableMobile } from "../../../Table";
import { TableTitle } from "../../Common/TableTitle";
import { useClientAccountReportContext } from "../providers/AccountReportProvider";
import { clientAccountColumns } from "./Columns";
import { MobileCardBody, MobileCardHeader } from "./MobileCard";

type ClientAccountTableProps = {
  isBackoffice?: boolean;
  translations: ReturnedTranslatedContentClientsPageHook;
  /** Gated by the part-3795-materialized-view-hoid flag. */
  showOfferIdColumn?: boolean;
};

export const ClientAccountTable = ({
  translations,
  isBackoffice = false,
  showOfferIdColumn = false,
}: ClientAccountTableProps) => {
  const { translatedContent, language } = translations;

  const {
    state: { page, pageSize, sortBy, sortOrder },
    resources: { table },
    actions: { setSort, setPage },
    runResource,
  } = useClientAccountReportContext();

  const { error } = table;
  const { accountList = [], redactedFields, totalCount } = table.data ?? {};
  const isLoading = table.status === "loading";

  const columnVisibility = useMemo(() => {
    return redactedFields?.length
      ? redactedFields.reduce<Record<string, boolean>>(
          (acc: Record<string, boolean>, field: string) => {
            acc[field] = false;
            return acc;
          },
          {},
        )
      : { clientId: false };
  }, [redactedFields]);

  const isMobile = useIsMobile();

  const sorting = useMemo(
    () =>
      sortBy != null && sortOrder != null
        ? [{ desc: sortOrder === "desc", id: sortBy }]
        : [],
    [sortOrder, sortBy],
  );

  const handleSortingUpdate = (updater: Updater<SortingState>) => {
    const nextSorting =
      typeof updater === "function" ? updater(sorting) : updater;
    const newSort = nextSorting[0];
    if (newSort.id !== sortBy) {
      setSort?.(newSort.id, "desc");
    } else {
      setSort?.(nextSorting[0].id, nextSorting[0].desc ? "desc" : "asc");
    }
  };

  const tableConfig = useMemo(
    () => ({
      translatedContent: translatedContent.accountReportTab.accountColumnLabels,
      language,
      columnVisibility,
      showOfferIdColumn,
    }),
    [translatedContent, language, columnVisibility, showOfferIdColumn],
  );

  const columns = clientAccountColumns({
    ...tableConfig,
    redactedFields,
    isBackoffice,
    showOfferIdColumn,
  });

  const handlePaginationChange = (
    updater: PaginationState | ((old: PaginationState) => PaginationState),
  ) => {
    const newPagination =
      typeof updater === "function"
        ? updater({
            pageIndex: page,
            pageSize,
          })
        : updater;

    setPage(newPagination.pageIndex);
  };

  const pageCount = Math.ceil((totalCount ?? 0) / (pageSize ?? 10));

  if (isMobile) {
    return (
      <div data-testid="mobile-table-content">
        <TableTitle />
        <TableMobile
          data={accountList}
          onRetry={() => runResource("table")}
          isError={Boolean(error)}
          columns={columns}
          cardHeader={MobileCardHeader(tableConfig)}
          cardBody={MobileCardBody(tableConfig)}
          showPagination
          translatedContent={translatedContent}
          filterFns={{} as any} // No client-side filter functions
          // Server-side pagination props
          isLoading={isLoading}
          enableServerSidePagination={true}
          pageCount={pageCount}
          totalCount={totalCount}
          state={{ pagination: { pageIndex: page, pageSize } }}
          onPaginationChange={handlePaginationChange}
        />
      </div>
    );
  }

  return (
    <TableDesktop
      data={accountList}
      onRetry={() => runResource("table")}
      isError={Boolean(error)}
      columns={columns}
      showPagination
      translatedContent={translatedContent}
      enableSorting
      enabledServerSideSorting
      sorting={sorting}
      onSortingChange={handleSortingUpdate}
      pageSize={pageSize}
      isLoading={isLoading}
      showHorizontalScroll
      stickyFirstColumn
      scrollFrame
      filterFns={{} as any} // No client-side filter functions
      // Server-side pagination props
      enableServerSidePagination={true}
      pageCount={pageCount}
      totalCount={totalCount}
      state={{ pagination: { pageIndex: page, pageSize } }}
      onPaginationChange={handlePaginationChange}
    />
  );
};
